import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { analyticsAPI } from '@/services/api';
import { Cloud, Filter } from 'lucide-react';

export default function SkillWordCloud() {
  const [skills, setSkills] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.getIndustries().then(d => setIndustries(d.industries || [])).catch(() => { /* noop */ });
  }, []);

  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      const d = await analyticsAPI.getSkillWordcloud(selectedIndustry);
      setSkills(d.skills || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedIndustry]);

  useEffect(() => { loadSkills(); }, [loadSkills]);

  const maxCount = useMemo(() => Math.max(...skills.map(s => s.count), 1), [skills]);

  const getSize = (count) => {
    const ratio = count / maxCount;
    if (ratio > 0.7) return 'text-2xl font-bold';
    if (ratio > 0.4) return 'text-lg font-semibold';
    if (ratio > 0.2) return 'text-base font-medium';
    return 'text-sm';
  };

  const getColor = (count) => {
    const ratio = count / maxCount;
    if (ratio > 0.7) return 'text-emerald-600 dark:text-emerald-400';
    if (ratio > 0.4) return 'text-blue-600 dark:text-blue-400';
    if (ratio > 0.2) return 'text-purple-600 dark:text-purple-400';
    return 'text-slate-500 dark:text-zinc-400';
  };

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="skill-word-cloud">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Cloud className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
          <h3 className="text-slate-900 dark:text-white font-medium text-sm">In-Demand Skills</h3>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-500" />
          <select value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)}
            className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-md px-2 py-1 text-xs text-slate-600 dark:text-zinc-300" data-testid="industry-filter">
            <option value="">All Industries</option>
            {industries.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-500 dark:text-zinc-500 text-sm">Loading skills...</div>
      ) : skills.length === 0 ? (
        <div className="py-8 text-center text-slate-500 dark:text-zinc-600 text-sm">No skill data available yet</div>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-2 items-baseline py-2" data-testid="word-cloud-words">
          {skills.slice(0, 50).map(({ skill, count }) => (
            <span key={skill} className={`${getSize(count)} ${getColor(count)} hover:text-slate-900 dark:hover:text-white transition-colors cursor-default`}
              title={`${count} job postings`} data-testid={`skill-${skill}`}>
              {skill}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
